import { useState } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Layers, Home, Building2 } from 'lucide-react';

const LEVEL_ICONS = [Layers, Home, Building2];

export default function FloorSection() {
  const { t } = useLanguage();
  const levels = t('floors.levels') || [];
  const [activeLevel] = useState(0);

  return (
    <section id="floors" className="py-24 md:py-32 lg:py-40 bg-[#050505]" data-testid="floor-section">
      <div className="px-6 md:px-12 lg:px-24 max-w-[1600px] mx-auto">
        <div className="text-center mb-16 md:mb-20 space-y-4">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#C8B598]">
            {t('floors.overline')}
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#F5F5F0] leading-tight">
            {t('floors.title')}
          </h2>
          <p className="text-base font-light text-[#9CA3AF] max-w-2xl mx-auto">
            {t('floors.subtitle')}
          </p>
        </div>

        {Array.isArray(levels) && levels.length > 0 && (
          <Tabs defaultValue="0" className="w-full">
            <TabsList className="w-full bg-[#0f1115] border border-white/5 h-auto p-1 grid grid-cols-3 gap-1" data-testid="floor-tabs">
              {levels.map((level, i) => {
                const Icon = LEVEL_ICONS[i] || Layers;
                return (
                  <TabsTrigger
                    key={i}
                    value={String(i)}
                    className="flex items-center gap-2 py-3 px-4 text-xs uppercase tracking-wider data-[state=active]:bg-[#C8B598] data-[state=active]:text-[#050505] text-[#9CA3AF] hover:text-[#F5F5F0] transition-all"
                    data-testid={`floor-tab-${i}`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="hidden sm:inline">{level.name}</span>
                    <span className="sm:hidden text-[10px]">{level.name}</span>
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {levels.map((level, i) => (
              <TabsContent key={i} value={String(i)} className="mt-8" data-testid={`floor-content-${i}`}>
                <div className="bg-[#0f1115] border border-white/5">
                  {/* Level header */}
                  <div className="p-6 md:p-8 border-b border-white/5">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div>
                        <h3 className="font-heading text-xl sm:text-2xl font-light text-[#F5F5F0]">
                          {level.name}
                        </h3>
                        <p className="text-sm text-[#9CA3AF] mt-1">{level.description}</p>
                      </div>
                      <div className="px-4 py-2 bg-[#C8B598]/10 border border-[#C8B598]/30 inline-flex items-center">
                        <span className="text-sm font-medium text-[#C8B598]">{level.area}</span>
                      </div>
                    </div>
                  </div>

                  {/* Rooms list */}
                  <ScrollArea className="max-h-[400px]">
                    <div className="divide-y divide-white/5">
                      {level.rooms && level.rooms.map((room, j) => (
                        <div
                          key={j}
                          className="flex items-center justify-between px-6 md:px-8 py-4 hover:bg-white/[0.02] transition-colors"
                          data-testid={`room-${i}-${j}`}
                        >
                          <span className="text-sm font-light text-[#F5F5F0]">{room.name}</span>
                          <span className="text-sm font-light text-[#C8B598] tabular-nums">{room.area}</span>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        )}
      </div>
    </section>
  );
}
